// Phase 32 Plan 32-03 Task 1 — useRevealFSM hook (RUNE-34).
//
// Shared 7-state Finite State Machine driving both FlopReveal + RiverReveal
// per UI-SPEC §4. Single source of truth so the two reveals can NEVER drift
// (T-32-07 mitigation).
//
// Standard-motion FSM timing (cumulative ms from mount):
//
//   t=0     scrim-in       (initial state at mount; effect kicks in
//                           synchronously and schedules the chain below)
//   t=200   punch-in       (300ms — Pixi camera scale 1.0 → 1.08)
//   t=500   drumroll       (400ms — scrim α 1.0 ↔ 0.95 oscillation)
//   t=900   floor-drop     (400ms — board-grow release beat; signals
//                           boardGrowReleased for the center-grow pulse)
//   t=1300  cell-cascade   (1200ms — increments cascadeFrontier 0..12 in
//                           12 setTimeout fires @ 100ms stagger)
//   t=1500  skipEnabled    (parallel — tap-to-skip affordance enabled)
//   t=2400  display-word   (600ms visible — "THE FLOP" / "THE RIVER")
//   t=3000  onComplete     (hard timeout — fires unconditionally regardless
//                           of intermediate FSM state; T-32-01 mitigation)
//
// Reduced-motion fork (Pitfall 4 mitigation — UI-SPEC §4 substitution table):
//   - Single window.setTimeout(onComplete, 200) at top of effect; return.
//   - No phase transitions beyond 'scrim-in' (no oscillation, no scale tween).
//   - cascadeFrontier stays 0 (no cascade — all 12 cells cross-fade
//     simultaneously per UI-SPEC reduced-motion table).
//   - skipEnabled stays false (the 200ms ceiling makes a skip pointless).
//   - Scrim wrapper consumers should keep pointerEvents='none' throughout.
//
// dismiss() contract:
//   - Calling dismiss() fires onComplete synchronously and drains all pending
//     setTimeouts so the auto-dismiss at t=3000 cannot double-fire.
//   - Idempotent — second call is a no-op (completedRef guard).
//   - Consumer (FlopReveal / RiverReveal) typically wires dismiss() to a
//     scrim onClick when skipEnabled === true (tap-to-skip after t=1500ms).
//
// Cleanup:
//   - useEffect return drains timersRef on unmount so no setState fires
//     against an unmounted component (no React warnings).
//
// Cascade frontier semantics (Pitfall 3 — sprite-not-found race mitigation):
//   - cascadeFrontier ∈ [0..12] is the COUNT of cells that have begun
//     spawning-in. The plan downstream wires LetterCubeSprite (Plan 32-05)
//     to consume the frontier via the $cascadeFrontier nanostore atom and
//     self-fire setState('spawning-in') when its cell_idx falls inside the
//     active window. This hook is the producer.

import { useStore } from '@nanostores/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { $reducedMotion } from '../../stores/motion'

/**
 * Discrete reveal-cinematic states. The component renders different DOM /
 * Pixi composition per phase; setting `data-phase={phase}` on the scrim
 * root makes the FSM observable in the DOM tree (test rig + Playwright).
 */
export type RevealPhase =
  | 'scrim-in'
  | 'punch-in'
  | 'drumroll'
  | 'floor-drop'
  | 'cell-cascade'
  | 'display-word'
  | 'dismissable'
  | 'unmount'

// ---------------------------------------------------------------------------
// Canonical timing constants (UI-SPEC §4 + Plan 32-03 frontmatter).
// All exported so tests + downstream consumers (Plan 32-05's
// LetterCubeSprite cascade gate) can pin-stitch against the same values.
// ---------------------------------------------------------------------------

/** Cumulative ms — hard auto-dismiss ceiling. */
export const TOTAL_STANDARD_MS = 3000
/** Cumulative ms — tap-to-skip enables. */
export const SKIP_ENABLED_AFTER_MS = 1500
/** Reduced-motion total — cross-fade only. */
export const REDUCED_FALLBACK_MS = 200
/** Per-cell stagger inside the cascade phase. */
export const CASCADE_STAGGER_MS = 100
/** Number of cells in a single growth wave (T6: cells 36-47; T11: 48-59). */
export const CASCADE_CELL_COUNT = 12
/** Cumulative ms — cascade phase starts (frontier flips 0 → 1). */
export const CASCADE_START_MS = 1300

export interface UseRevealFSMArgs {
  readonly onComplete: () => void
}

export interface UseRevealFSMResult {
  /** Current FSM state — see RevealPhase comments. */
  readonly phase: RevealPhase
  /** True once tap-to-skip is enabled (t ≥ 1500ms in standard motion).
   *  Always false under reduced motion. */
  readonly skipEnabled: boolean
  /** Count of cells that have begun spawning-in [0..CASCADE_CELL_COUNT].
   *  Drives LetterCubeSprite spawn-in gating via $cascadeFrontier
   *  (Plan 32-05). Always 0 under reduced motion. */
  readonly cascadeFrontier: number
  /** True once the floor-drop beat fires (t ≥ 900ms in standard motion).
   *  Signals existing LetterCubeSprite cubes to start their old→new anchor
   *  glide, overlapping the new-cell cascade ($boardReanchor, Task 4/5).
   *  Always false under reduced motion (consumer snaps immediately). */
  readonly boardGrowReleased: boolean
  /** Imperative dismiss — fires onComplete + drains remaining timers.
   *  Typically wired to the scrim onClick when skipEnabled === true. */
  readonly dismiss: () => void
  /** Mirrors $reducedMotion at mount (rebuilt on toggle via dep array). */
  readonly reducedMotion: boolean
}

/**
 * Drives the 7-state reveal cinematic FSM. Single source of truth for
 * FlopReveal + RiverReveal (T-32-07).
 *
 * Standard motion: 3000ms total cinematic with tap-to-skip after 1500ms.
 * Reduced motion: 200ms cross-fade only — Pitfall 4 clean-fork early return.
 *
 * @param args.onComplete - Fired exactly once when the cinematic concludes
 *   (auto-dismiss at t=3000ms, manual dismiss(), or reduced-motion 200ms).
 */
export function useRevealFSM({
  onComplete,
}: UseRevealFSMArgs): UseRevealFSMResult {
  const reduced = useStore($reducedMotion)
  const [phase, setPhase] = useState<RevealPhase>('scrim-in')
  const [skipEnabled, setSkipEnabled] = useState(false)
  const [cascadeFrontier, setCascadeFrontier] = useState(0)
  const [boardGrowReleased, setBoardGrowReleased] = useState(false)

  // completedRef + timersRef ride in refs so dismiss() (a stable callback)
  // and the effect cleanup share state without re-running the FSM.
  const completedRef = useRef(false)
  const timersRef = useRef<number[]>([])
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete

  /** Drain pending timers + fire onComplete once. Idempotent. */
  const fireComplete = useCallback((): void => {
    if (completedRef.current) return
    completedRef.current = true
    for (const t of timersRef.current) window.clearTimeout(t)
    timersRef.current = []
    onCompleteRef.current()
  }, [])

  const dismiss = useCallback((): void => {
    fireComplete()
  }, [fireComplete])

  useEffect(() => {
    // Re-arm on every mount / reduced-motion toggle. The cleanup below
    // drains any prior timers so a toggle mid-sequence doesn't double-fire.
    completedRef.current = false
    setBoardGrowReleased(false)

    const schedule = (ms: number, fn: () => void): void => {
      const t = window.setTimeout(fn, ms)
      timersRef.current.push(t)
    }

    if (reduced) {
      // Pitfall 4 clean fork — no FSM, no cascade, no skip affordance.
      // The 200ms cross-fade matches UI-SPEC §4 reduced-motion table.
      schedule(REDUCED_FALLBACK_MS, fireComplete)
      return (): void => {
        for (const t of timersRef.current) window.clearTimeout(t)
        timersRef.current = []
      }
    }

    // ---- Standard-motion FSM ------------------------------------------------
    // Note: `scrim-in` is the initial useState value, so no setPhase('scrim-in')
    // timer is needed at t=0. The first transition (→ punch-in) fires at t=200.
    schedule(200, () => setPhase('punch-in'))
    schedule(500, () => setPhase('drumroll'))
    schedule(900, () => setPhase('floor-drop'))
    schedule(900, () => setBoardGrowReleased(true))
    schedule(CASCADE_START_MS, () => setPhase('cell-cascade'))

    // Tap-to-skip gate — parallel to phase ladder.
    schedule(SKIP_ENABLED_AFTER_MS, () => setSkipEnabled(true))

    // 12-cell cascade frontier — 100ms stagger, frontier 1 at t=1300,
    // frontier 12 at t=2400 (Pitfall 3 mitigation: sprite-side consumer
    // gates spawn-in on the frontier window).
    for (let i = 0; i < CASCADE_CELL_COUNT; i++) {
      const fireAt = CASCADE_START_MS + i * CASCADE_STAGGER_MS
      schedule(fireAt, () => setCascadeFrontier(i + 1))
    }

    // Display-word phase — 600ms visible window before hard timeout.
    schedule(2400, () => setPhase('display-word'))

    // Hard timeout — fires onComplete regardless of intermediate FSM
    // (T-32-01 mitigation: cinematic never blocks input indefinitely).
    schedule(TOTAL_STANDARD_MS, fireComplete)

    return (): void => {
      for (const t of timersRef.current) window.clearTimeout(t)
      timersRef.current = []
    }
  }, [reduced, fireComplete])

  return {
    phase,
    skipEnabled,
    cascadeFrontier,
    boardGrowReleased,
    dismiss,
    reducedMotion: reduced,
  }
}
